import type { InitiateSubscriptionPaymentRequest } from '../models/subscription-payment.model';

export type SubscriptionPlanPricingByCycle = {
  pricing: {
    monthly: number;
    quarterly: number | null;
    yearly: number;
  };
};

/** Matches `PlatformBillingService` subscription invoice GST (18%). */
export const SUBSCRIPTION_GST_RATE = 18;

export type SubscriptionPaymentAmountBreakdown = {
  baseAmount: number;
  gstRate: number;
  gstAmount: number;
  amount: number;
};

export type SubscriptionPaymentConfirmState = {
  title: string;
  description: string;
  /** Total payable amount including GST. */
  amount: number;
  baseAmount: number;
  gstRate: number;
  gstAmount: number;
  billingLabel?: string;
  initiateRequest: InitiateSubscriptionPaymentRequest;
  processingKey: string;
};

export function planAmountForCycle(
  plan: SubscriptionPlanPricingByCycle,
  cycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
): number {
  if (cycle === 'YEARLY') {
    return plan.pricing.yearly;
  }
  if (cycle === 'QUARTERLY' && plan.pricing.quarterly) {
    return plan.pricing.quarterly;
  }
  return plan.pricing.monthly;
}

function isSameBillingState(
  customerState: string,
  platformState = 'Maharashtra',
): boolean {
  const customer = customerState.trim().toLowerCase();
  const platform = platformState.trim().toLowerCase();
  return customer.includes(platform) || platform.includes(customer);
}

/** Mirrors backend `splitGst` for subscription checkout previews. */
export function computeSubscriptionPaymentAmountWithGst(
  baseAmount: number,
  billingState?: string | null,
): SubscriptionPaymentAmountBreakdown {
  const taxable = Math.max(0, Number.isFinite(baseAmount) ? baseAmount : 0);
  const customerState = billingState?.trim() ?? 'Maharashtra';
  const gstAmountRaw = (taxable * SUBSCRIPTION_GST_RATE) / 100;
  const gstAmount = isSameBillingState(customerState)
    ? Math.round((gstAmountRaw / 2) * 100) / 100 * 2
    : Math.round(gstAmountRaw * 100) / 100;

  return {
    baseAmount: taxable,
    gstRate: SUBSCRIPTION_GST_RATE,
    gstAmount,
    amount: taxable + gstAmount,
  };
}

export function buildSubscriptionPaymentConfirmAmounts(
  baseAmount: number,
  billingState?: string | null,
): SubscriptionPaymentAmountBreakdown {
  return computeSubscriptionPaymentAmountWithGst(baseAmount, billingState);
}

export function billingCycleLabel(
  cycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
): string {
  switch (cycle) {
    case 'YEARLY':
      return 'Yearly';
    case 'QUARTERLY':
      return 'Quarterly';
    default:
      return 'Monthly';
  }
}
